import React, { useEffect, useMemo, useState } from "react";
import ReactApexChart from "react-apexcharts";
import { getFormsByTypeSummary } from "../../api/work-order-formsAPI";

const colorPalette = [
  "#6366F1",
  "#22C55E",
  "#F97316",
  "#0EA5E9",
  "#F59E0B",
  "#8B5CF6",
  "#EC4899",
  "#14B8A6",
];

const FormsByTypeChart = () => {
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        const response = await getFormsByTypeSummary();
        setData(Array.isArray(response) ? response : []);
      } catch (err) {
        console.error("Failed to load forms by type summary", err);
        setError("Unable to load form type summary");
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  const { series, options } = useMemo(() => {
    if (!data.length) {
      return {
        series: [],
        options: {
          labels: [],
          chart: { type: "donut" },
        },
      };
    }

    const labels = data.map((item) => item.name ?? "Unknown");
    const seriesData = data.map((item) => Number(item.count ?? 0));
    const colors = labels.map((_, index) => colorPalette[index % colorPalette.length]);

    return {
      series: seriesData,
      options: {
        labels,
        colors,
        chart: {
          type: "donut",
        },
        legend: { show: false },
        dataLabels: { enabled: false },
        tooltip: {
          y: {
            formatter: (value) => `${value} forms`,
          },
        },
      },
    };
  }, [data]);

  return (
    <div className='col-xxl-4'>
      <div className='card h-100 radius-8 border-0'>
        <div className='card-body p-24 d-flex flex-column gap-16'>
          <div className='d-flex align-items-center justify-content-between'>
            <h6 className='mb-0 fw-bold text-lg'>Completed Forms by Type</h6>
          </div>
          {isLoading ? (
            <div className='flex-grow-1 d-flex align-items-center justify-content-center'>
              <span className='text-secondary-600 text-sm'>Loading chart...</span>
            </div>
          ) : error ? (
            <div className='flex-grow-1 d-flex align-items-center justify-content-center'>
              <span className='text-danger-main text-sm'>{error}</span>
            </div>
          ) : data.length === 0 ? (
            <div className='flex-grow-1 d-flex align-items-center justify-content-center'>
              <span className='text-secondary-600 text-sm'>No completed forms to display.</span>
            </div>
          ) : (
            <>
              <ReactApexChart options={options} series={series} type='donut' height={270} />
              <ul className='d-flex flex-wrap align-items-center justify-content-between mt-3 gap-3'>
                {data.map((item, index) => (
                  <li key={`${item.name}-${index}`} className='d-flex flex-column gap-8'>
                    <div className='d-flex align-items-center gap-2'>
                      <span
                        className='w-12-px h-12-px rounded-circle'
                        style={{ backgroundColor: colorPalette[index % colorPalette.length] }}
                      />
                      <span className='text-secondary-light text-sm fw-semibold'>
                        {item.name ?? "Unknown"}
                      </span>
                    </div>
                    <span className='text-primary-light fw-bold'>{Number(item.count ?? 0)}</span>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default FormsByTypeChart;
